import type { OrderDetail } from '../types';

interface OrderDetailModalProps {
  order: OrderDetail | null;
  onClose: () => void;
}

export default function OrderDetailModal({ order, onClose }: OrderDetailModalProps) {
  if (!order) return null;

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      '待支付': 'text-orange-600 bg-orange-50',
      '已支付': 'text-blue-600 bg-blue-50',
      '已取消': 'text-gray-600 bg-gray-50',
      '已退款': 'text-red-600 bg-red-50',
      '部分退款': 'text-purple-600 bg-purple-50',
      '已完成': 'text-green-600 bg-green-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">订单详情</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 订单信息 */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-4">订单信息</h4>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">订单号</div>
                <div className="text-sm font-medium text-gray-900">{order.orderNo}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">支付单号</div>
                <div className="text-sm font-medium text-gray-900">{order.paymentNo || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">下单类型</div>
                <div className="text-sm font-medium text-gray-900">{order.orderType}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">下单用户</div>
                <div className="text-sm font-medium text-gray-900">{order.userName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">手机号码</div>
                <div className="text-sm font-medium text-gray-900">{order.userPhone}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">下单时间</div>
                <div className="text-sm font-medium text-gray-900">{order.orderTime}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">订单状态</div>
                <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>
            </div>
          </div>

          {/* 商品信息 */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-4">商品信息</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">商品名称</div>
                  <div className="text-sm font-medium text-gray-900">{order.productName}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">数量</div>
                  <div className="text-sm font-medium text-gray-900">{order.quantity}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">金额</div>
                  <div className="text-sm font-medium text-gray-900">¥{order.orderAmount.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">付费方式</div>
                  <div className="text-sm font-medium text-gray-900">{order.paymentMethodDetail}</div>
                </div>
              </div>

              {order.configDetail && (
                <div>
                  <div className="text-sm text-gray-500 mb-2">配置详情</div>
                  <div className="bg-white rounded-lg p-3 space-y-2">
                    {order.configDetail.productName && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">商品名称：</span>
                        <span className="text-gray-900">{order.configDetail.productName}</span>
                      </div>
                    )}
                    {order.configDetail.bandwidth && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">宽带网络：</span>
                        <span className="text-gray-900">{order.configDetail.bandwidth}</span>
                      </div>
                    )}
                    {order.configDetail.diskType && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">系统盘类型：</span>
                        <span className="text-gray-900">{order.configDetail.diskType}</span>
                      </div>
                    )}
                    {order.configDetail.diskCapacity && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">系统盘容量：</span>
                        <span className="text-gray-900">{order.configDetail.diskCapacity}</span>
                      </div>
                    )}
                    {order.configDetail.cpu && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">CPU：</span>
                        <span className="text-gray-900">{order.configDetail.cpu}</span>
                      </div>
                    )}
                    {order.configDetail.memory && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">内存：</span>
                        <span className="text-gray-900">{order.configDetail.memory}</span>
                      </div>
                    )}
                    {order.configDetail.gpu && (
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 w-24">显卡：</span>
                        <span className="text-gray-900">{order.configDetail.gpu}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {order.discount && (
                <div>
                  <div className="text-sm text-gray-500 mb-2">优惠活动</div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-sm text-teal-600">
                      {order.discount.type === 'gift' && `赠送：${order.discount.value}`}
                      {order.discount.type === 'discount' && `折扣：${order.discount.value}`}
                      {order.discount.type === 'reduction' && `减免：${order.discount.value}`}
                    </div>
                  </div>
                </div>
              )}

              {order.startTime && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">开始时间</div>
                    <div className="text-sm font-medium text-gray-900">{order.startTime}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">结束时间</div>
                    <div className="text-sm font-medium text-gray-900">{order.endTime}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">释放时间</div>
                    <div className="text-sm font-medium text-gray-900">{order.releaseTime}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 支付信息 */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-4">支付信息</h4>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">支付方式</div>
                <div className="text-sm font-medium text-gray-900">{order.paymentChannel}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">订单金额</div>
                <div className="text-sm font-medium text-gray-900">¥{order.orderAmount.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">优惠金额</div>
                <div className="text-sm font-medium text-teal-600">-¥{order.discountAmount.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">实收金额</div>
                <div className="text-sm font-medium text-gray-900">¥{order.actualAmount.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">支付时间</div>
                <div className="text-sm font-medium text-gray-900">{order.paymentTime || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 whitespace-nowrap"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
